import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  buildVisitorSignInboxDocumentPath,
  fetchSignatureInvite,
  registerFromSignatureInvite,
  type PublicSignatureInvite,
} from '../../services/signatureInviteService';
import { Button } from '../../components/UI/Button';
import { extractCpfDigits, formatCpfInput } from '../../utils/cpfMask';

export const SignatureInvitePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [invite, setInvite] = useState<PublicSignatureInvite | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  const [registerName, setRegisterName] = useState('');
  const [registerCpf, setRegisterCpf] = useState('');
  const [registerBirthDate, setRegisterBirthDate] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirm, setRegisterConfirm] = useState('');
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registerBusy, setRegisterBusy] = useState(false);

  const load = useCallback(async () => {
    if (!token)
    {
      setLoadError('Link inválido.');
      setBusy(false);
      return;
    }

    setBusy(true);
    setLoadError(null);
    try
    {
      const data = await fetchSignatureInvite(token);
      setInvite(data);
      if (data.signerName)
      {
        setRegisterName(data.signerName);
      }
    }
    catch
    {
      setLoadError('Este convite não é válido ou já expirou.');
      setInvite(null);
    }
    finally
    {
      setBusy(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!invite || !token)
    {
      return;
    }

    if (isAuthenticated)
    {
      navigate(buildVisitorSignInboxDocumentPath(String(invite.documentId ?? ''), token), { replace: true });
    }
  }, [invite, isAuthenticated, navigate, token]);

  const onRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token)
    {
      return;
    }

    setRegisterError(null);
    setRegisterBusy(true);
    try
    {
      const cpfDigits = extractCpfDigits(registerCpf);
      if (cpfDigits.length !== 11)
      {
        setRegisterError('Informe um CPF válido com 11 dígitos.');
        setRegisterBusy(false);
        return;
      }

      if (!registerBirthDate.trim())
      {
        setRegisterError('Informe a data de nascimento.');
        setRegisterBusy(false);
        return;
      }

      const result = await registerFromSignatureInvite(token, {
        name: registerName.trim(),
        password: registerPassword,
        confirmPassword: registerConfirm,
        cpf: cpfDigits,
        birthDate: registerBirthDate.trim(),
      });
      const docId = String(result.documentId ?? invite?.documentId ?? '');
      const returnUrl = encodeURIComponent(buildVisitorSignInboxDocumentPath(docId, token));
      navigate(`/login?returnUrl=${returnUrl}`, { replace: true });
    }
    catch (err: unknown)
    {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setRegisterError(typeof msg === 'string' ? msg : 'Não foi possível concluir o cadastro.');
    }
    finally
    {
      setRegisterBusy(false);
    }
  };

  const goLogin = () => {
    if (!invite || !token)
    {
      return;
    }

    const returnUrl = encodeURIComponent(
      buildVisitorSignInboxDocumentPath(String(invite.documentId ?? ''), token)
    );
    navigate(`/login?returnUrl=${returnUrl}`);
  };

  const documentLabel = invite?.documentTitle || invite?.documentId || 'Documento';

  if (busy)
  {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <p className="text-slate-600">Carregando convite…</p>
      </div>
    );
  }

  if (loadError || !invite)
  {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 px-4">
        <div className="mx-auto w-full max-w-xl rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm sm:p-10">
          <h1 className="text-lg font-semibold text-slate-900">Convite indisponível</h1>
          <p className="mt-2 text-sm text-slate-600">{loadError ?? 'Não foi possível abrir este convite.'}</p>
          <p className="mt-3 text-sm text-slate-500">
            Verifique se o link está completo ou solicite um novo convite a quem enviou o documento.
          </p>
          <Link to="/login" className="mt-6 inline-block text-sm font-medium text-blue-600 hover:text-blue-800">
            Ir para o login
          </Link>
        </div>
      </div>
    );
  }

  if (isAuthenticated)
  {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <p className="text-slate-600">Redirecionando para o documento…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-2xl">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
          <h1 className="text-2xl font-semibold text-slate-900">Você foi convidado a assinar</h1>
          <p className="mt-2 max-w-3xl text-base text-slate-600">
            {invite.requiresRegistration
              ? 'Complete seu cadastro para acessar o documento e assinar com segurança.'
              : 'Faça login para acessar o documento e concluir a assinatura.'}
          </p>

          <div className="mt-8 grid gap-5 rounded-lg border border-slate-200 bg-slate-50 p-5 sm:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Documento</p>
              <p className="mt-1 text-sm font-medium text-slate-900">{documentLabel}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Convite enviado para</p>
              <p className="mt-1 text-sm text-slate-700">
                {invite.signerName ? (
                  <>
                    {invite.signerName}
                    <span className="text-slate-500"> · {invite.signerEmailMasked}</span>
                  </>
                ) : (
                  invite.signerEmailMasked
                )}
              </p>
            </div>
          </div>

          {invite.requiresRegistration ? (
            <form className="mt-8 space-y-8" onSubmit={onRegister}>
              <div className="rounded-lg border border-blue-100 bg-blue-50 p-5">
                <p className="text-sm font-medium text-blue-900">Verificação de identidade</p>
                <p className="mt-1 text-sm leading-relaxed text-blue-800">
                  Seu CPF e data de nascimento serão confirmados novamente no momento da assinatura, para garantir
                  que apenas você possa assinar este documento.
                </p>
              </div>

              <fieldset className="m-0 space-y-5 border-0 p-0">
                <legend className="mb-1 text-base font-medium text-slate-900">Seus dados</legend>
                <div>
                  <label className="block text-sm font-medium text-slate-700" htmlFor="reg-name">
                    Nome completo
                  </label>
                  <input
                    id="reg-name"
                    className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={registerName}
                    onChange={(ev) => setRegisterName(ev.target.value)}
                    required
                    autoComplete="name"
                  />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700" htmlFor="reg-cpf">
                      CPF
                    </label>
                    <input
                      id="reg-cpf"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="000.000.000-00"
                      maxLength={14}
                      className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={registerCpf}
                      onChange={(ev) => setRegisterCpf(formatCpfInput(ev.target.value))}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700" htmlFor="reg-birth">
                      Data de nascimento
                    </label>
                    <input
                      id="reg-birth"
                      type="date"
                      className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={registerBirthDate}
                      onChange={(ev) => setRegisterBirthDate(ev.target.value)}
                      required
                    />
                  </div>
                </div>
              </fieldset>

              <fieldset className="m-0 space-y-5 border-0 p-0">
                <legend className="mb-1 text-base font-medium text-slate-900">Senha de acesso</legend>
                <p className="text-sm text-slate-500">
                  Use esta senha para entrar e acessar documentos enviados a você.
                </p>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700" htmlFor="reg-pass">
                      Senha
                    </label>
                    <input
                      id="reg-pass"
                      type="password"
                      className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={registerPassword}
                      onChange={(ev) => setRegisterPassword(ev.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700" htmlFor="reg-pass2">
                      Confirmar senha
                    </label>
                    <input
                      id="reg-pass2"
                      type="password"
                      className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={registerConfirm}
                      onChange={(ev) => setRegisterConfirm(ev.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                  </div>
                </div>
              </fieldset>

              {registerError ? <p className="text-sm text-red-600">{registerError}</p> : null}

              <div className="space-y-4 pt-2">
                <Button type="submit" disabled={registerBusy} className="w-full py-3 text-base">
                  {registerBusy ? 'Enviando…' : 'Continuar para assinar'}
                </Button>

                <p className="text-center text-sm text-slate-600">
                  Já possui conta?{' '}
                  <button
                    type="button"
                    onClick={goLogin}
                    className="font-medium text-blue-600 hover:text-blue-800"
                  >
                    Entrar
                  </button>
                </p>
              </div>
            </form>
          ) : (
            <div className="mt-8 space-y-5">
              <p className="text-base text-slate-600">
                Encontramos uma conta associada ao e-mail{' '}
                <span className="font-medium text-slate-800">{invite.signerEmailMasked}</span>. Entre para continuar.
              </p>
              <Button type="button" onClick={goLogin} className="w-full py-3 text-base">
                Entrar para assinar
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
